import { useState, useEffect } from "react"
import { FormSectionWrapper } from "../FormSectionWrapper"
import { AdditionalReferences } from "./Sections/AdditionalReferences"
import { BillingDetails } from "./Sections/BillingDetails"
import { PackageAndShipmentDetails } from "./Sections/PackageAndShipmentDetails"
import { RecipientInformation } from "./Sections/RecipientInformation"
import { SenderInformation } from "./Sections/SenderInformation"
import { Shortcuts } from "./Sections/Shortcuts"
import { GetCountries, GetState, GetCity } from 'react-country-state-city';
import { initialFormData } from '../../utils/formikFields';
import { getLocalDateTimeString } from "../../utils/localDateTime"

export const ShipmentDetails = ({ formik, getSpecialServices, specialServicesLoading, formFields, tabKey }) => {
    const [countries, setCountries] = useState([]);
    const [states, setStates] = useState([]);
    const [cities, setCities] = useState([]);

    useEffect(() => {
        const getCountries = async () => {
            const countries = await GetCountries();
            setCountries(countries);
            setStatesDropdown(countries);
        }
        const setStatesDropdown = async (countries) => {
            const defaultCountry = countries.find((c) => c.iso2 === 'US');
            const statesData = await GetState(defaultCountry.id);
            setStates(statesData);
        }

        getCountries();
    }, []);

    useEffect(() => {
        const setFormData = async () => {
            const formDataObj = formFields.reduce((obj, currObj) => {
                if (!Object.keys(currObj).includes('embeddedObject')) {
                    return { ...obj, ...currObj }
                } else {
                    return obj
                }
            }, {});

            const today = getLocalDateTimeString();
            if (!formDataObj.ShipDate || formDataObj.ShipDate < today) {
                formDataObj.ShipDate = today;
            }

            if (formDataObj.ShipToCountry === 'Korea, Republic Of') {
                formDataObj.ShipToCountry = 'South Korea';
            }

            formik.setValues({
                ...initialFormData,
                orderNum: formik.values.orderNum, // spreading intialValues up above, so resetting the orderNum value so it's not lost in the reset
                // Recipient information
                country: getCountryCode(formDataObj.ShipToCountry),
                contactName: formDataObj.ShipToName,
                companyName: formDataObj.ShipToCompany.slice(0, 35),
                address1: formDataObj.ShipToAddrLine1,
                address2: formDataObj.ShipToAddrLine2,
                address3: formDataObj.ShipToAddrLine3,
                postalCode: formDataObj.ShipToZipCode,
                stateProvince: formDataObj.ShipToState,
                city: formDataObj.ShipToCity,
                telephone: formDataObj.ShipToAreaCode + formDataObj.ShipToPhone,
                email: formDataObj.ShipToEmail,
                ext: formDataObj.ShipToPhoneExtension,
                shipAlerts: {
                    ...initialFormData.shipAlerts,
                    recipient: {
                        ...initialFormData.shipAlerts.recipient,
                        emailAddress: formDataObj.ShipToEmail,
                        notificationEventType: ['ON_SHIPMENT', 'ON_EXCEPTION', 'ON_DELIVERY']
                    }
                },
                // Sender information
                // Package and shipment details
                shipDate: formDataObj.ShipDate.slice(0, 10),
                // Billing details
                poNum: formDataObj.InvoiceNumber,
                // Additional references
                invoiceNum: formDataObj.InvoiceNumber
            })

            updateStateCityDropdowns(formDataObj.ShipToCountry, formDataObj.ShipToState);
        }

        if (formFields.length > 0) {
            setFormData();
        }
    }, [formFields])

    const handleCountryChange = async (e) => {
        formik.handleChange(e);
        setCities([]);
        const selectedCountry = countries.find((c) => c.iso2 === e.target.value);
        if (!selectedCountry) return setStates([]);
        const statesData = await GetState(selectedCountry.id);
        setStates(statesData);
        formik.setFieldValue('stateProvince', '');
        formik.setFieldValue('city', '');
    }

    const handleStateChange = async (e) => {
        formik.handleChange(e);
        const selectedState = states.find((s) => s.state_code === e.target.value);
        if (!selectedState) return setCities([]);
        const selectedCountry = countries.find((c) => c.iso2 === formik.values.country);
        const citiesData = await GetCity(selectedCountry.id, selectedState.id);
        setCities(citiesData);
        formik.setFieldValue('city', '');
    }

    const getCountryCode = (countryName) => {
        const countryObj = countries.find((c) => c.name === countryName);
        const countryCode = countryObj.iso2;
        return countryCode;
    }

    const updateStateCityDropdowns = async (countryName, stateCode) => {
        const countryObj = countries.find((c) => c.name === countryName);
        const statesData = await GetState(countryObj.id);
        setStates(statesData);
        if (stateCode) {
            const stateObj = statesData.find((s) => s.state_code === stateCode);
            const citiesData = await GetCity(countryObj.id, stateObj.id);
            setCities(citiesData);
        } else if (cities.length) {
            setCities([]); // clear the cities dropdown if old data is present
        }
    }

    return (
        <>
            <div className='form-grid'>
                <div>
                    <FormSectionWrapper title='Recipient information'>
                        <RecipientInformation
                            formik={formik}
                            countries={countries}
                            states={states}
                            cities={cities}
                            handleCountryChange={handleCountryChange}
                            handleStateChange={handleStateChange}
                            isReturnShipmentTab={false}
                            tabKey={tabKey}
                        />
                    </FormSectionWrapper>
                    <FormSectionWrapper title='Sender information'>
                        <SenderInformation formik={formik} isReturnShipmentTab={false} />
                    </FormSectionWrapper>
                </div>
                <div>
                    <FormSectionWrapper title='Package and shipment details'>
                        <PackageAndShipmentDetails
                            formik={formik}
                            isReturnShipmentTab={false}
                        />
                    </FormSectionWrapper>
                    <FormSectionWrapper title='Billing details'>
                        <BillingDetails formik={formik} isReturnShipmentTab={false} />
                    </FormSectionWrapper>
                    <FormSectionWrapper title='Additional references'>
                        <AdditionalReferences formik={formik} isReturnShipmentTab={false} />
                    </FormSectionWrapper>
                </div>
                <div>
                    <FormSectionWrapper title='Shortcuts'>
                        <Shortcuts
                            formik={formik}
                            getSpecialServices={getSpecialServices}
                            specialServicesLoading={specialServicesLoading}
                            isReturnShipmentTab={false}
                        />
                    </FormSectionWrapper>
                </div>
            </div>
        </>
    )
}